import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, DatePicker, Space, Table, Tag, Tooltip, Typography, message } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import moment from "moment";
import APIService from "services/ApiService";
import useJobPolling from "hooks/useJobPolling";
import { isActive } from "hooks/jobPolling";
import JobStatusTag from "components/jobs/JobStatusTag";
import {
  BatchJob,
  HistoryGapGroup,
  HistoryGapRecoverJob,
  HistoryGapResult,
  HistoryGapState,
} from "reducers/types";

const api = new APIService();

const JOBS = [
  { type: "cmc-history", label: "Crypto", basePath: "/admin/resources/update/cmc/history" },
  { type: "polygon-history", label: "Nasdaq", basePath: "/admin/resources/update/polygon/history" },
  { type: "tefas-history", label: "Funds", basePath: "/admin/resources/update/tefas/history" },
];

const POLL_MS = 2000;
const FOLLOW_UP_MS = 30000; // after a job ends, look for the refreshed result this long

const pct = (ratio: number) => `${(ratio * 100).toFixed(2)} %`;

const loadState = (): Promise<HistoryGapState> =>
  api.get("/admin/history-gaps").then((res: any) => {
    if (!res || !res.result) {
      throw new Error("BAD_RESPONSE");
    }
    return res.result as HistoryGapState;
  });

// History gaps (T005): coverage of the last weekdays per recoverable group, abnormal dates, one-click recovery
// (TEFAS + CoinMarketCap + Polygon jobs, never overriding). The check runs on the server only when asked or after a
// recovery job ended; opening the tab just reads the cached result.
const HistoryGapsCard: React.FC = () => {
  const [state, setState] = useState<HistoryGapState | null>(null);
  const [shown, setShown] = useState<HistoryGapResult | null>(null); // last result received, kept while a new one is computed
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<[moment.Moment | null, moment.Moment | null] | null>(null);
  const [recovering, setRecovering] = useState(false);
  const [recoverResult, setRecoverResult] = useState<HistoryGapRecoverJob[] | null>(null);
  const [followUntil, setFollowUntil] = useState(0);
  const followFrom = useRef(0);
  const mounted = useRef(true);

  const cmc = useJobPolling({ basePath: JOBS[0].basePath });
  const polygon = useJobPolling({ basePath: JOBS[1].basePath });
  const tefas = useJobPolling({ basePath: JOBS[2].basePath });
  const jobList: Array<BatchJob | null> = [cmc.job, polygon.job, tefas.job];
  const anyActive = jobList.some((j) => j != null && isActive(j.status));

  const apply = useCallback((next: HistoryGapState) => {
    if (!mounted.current) {
      return;
    }
    setState(next);
    setStale(next.stale);
    setError(next.state === "FAILED" ? next.error || "The check failed" : null);
    if (next.state === "READY" && next.result) {
      setShown(next.result);
    }
  }, []);

  const refresh = useCallback(() => {
    loadState()
      .then(apply)
      .catch(() => mounted.current && setError("Could not read the history gaps state"));
  }, [apply]);

  useEffect(() => {
    mounted.current = true;
    refresh(); // page open: one cache read, no computation
    return () => {
      mounted.current = false;
    };
  }, [refresh]);

  // A job of any of the three types turning terminal: the server already evicted and recomputes; follow it.
  const lastStatuses = useRef<{ [id: string]: string }>({});
  useEffect(() => {
    jobList.forEach((job) => {
      if (!job) {
        return;
      }
      const before = lastStatuses.current[job.jobId];
      lastStatuses.current[job.jobId] = job.status;
      if (before && isActive(before as any) && !isActive(job.status)) {
        followFrom.current = Date.now();
        setFollowUntil(Date.now() + FOLLOW_UP_MS);
      }
    });
  }); // runs after every render on purpose: compares statuses seen so far

  const computing = state?.state === "COMPUTING";
  const following = followUntil > Date.now() && !(state?.state === "READY" && state.result && state.result.computedAt >= followFrom.current);

  useEffect(() => {
    if (!computing && !following) {
      return;
    }
    const t = setTimeout(refresh, POLL_MS);
    return () => clearTimeout(t);
  }, [computing, following, state, refresh]);

  const check = () => {
    setError(null);
    api
      .post("/admin/history-gaps/check")
      .then((res: any) => apply(res.result as HistoryGapState))
      .catch(() => setError("The check could not be started"));
  };

  const abnormal = new Set(shown ? shown.abnormalDates : []);

  const recover = () => {
    if (!range || !range[0] || !range[1]) {
      return;
    }
    setRecovering(true);
    setRecoverResult(null);
    api
      .post("/admin/history-gaps/recover", { from: range[0].format("YYYY-MM-DD"), to: range[1].format("YYYY-MM-DD") })
      .then((res: any) => {
        setRecoverResult((res.result && res.result.jobs) || []);
        followFrom.current = Date.now();
        setFollowUntil(Date.now() + FOLLOW_UP_MS);
      })
      .catch(() => message.error("Recovery could not be started"))
      .finally(() => mounted.current && setRecovering(false));
  };

  const columns = [
    {
      title: "Group (assets)",
      key: "group",
      render: (_: any, g: HistoryGapGroup) => `${g.label} (${g.selection.toLocaleString()})`,
    },
    ...(shown ? shown.dates : []).map((date, i) => ({
      title: moment(date).format("ddd MM-DD"),
      key: date,
      render: (_: any, g: HistoryGapGroup) => {
        const d = g.days[i];
        if (!d) {
          return null;
        }
        const text = `${d.count.toLocaleString()} · ${pct(d.ratio)}`;
        return d.abnormal ? (
          <Tooltip title={`below the group threshold ${pct(g.threshold)}`}>
            <Typography.Text type="danger">
              {text} <WarningOutlined />
            </Typography.Text>
          </Tooltip>
        ) : (
          text
        );
      },
    })),
    { title: "Mean", key: "mean", render: (_: any, g: HistoryGapGroup) => pct(g.mean) },
    { title: "Threshold", key: "threshold", render: (_: any, g: HistoryGapGroup) => pct(g.threshold) },
  ];

  const updating = computing || following;

  return (
    <Card
      title="History gaps"
      extra={
        <Button type="primary" onClick={check} loading={computing}>
          Check
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Typography.Text type="secondary">
          Last 5 weekdays up to yesterday; weekends are not checked. A date is abnormal when a group's coverage is below
          its threshold.
        </Typography.Text>
        {error ? <Alert type="error" showIcon message={error} /> : null}
        {stale ? (
          <Alert type="warning" showIcon message="This result was computed for an earlier window; press Check to refresh." />
        ) : null}
        {shown ? (
          <Typography.Text>
            Computed {moment(shown.computedAt).format("YYYY-MM-DD HH:mm")} ·{" "}
            {shown.abnormalDates.length > 0
              ? `${shown.abnormalDates.length} abnormal date(s): ${shown.abnormalDates.join(", ")}`
              : "no abnormal dates"}
            {updating ? <Tag color="blue" style={{ marginLeft: 8 }}>Updating…</Tag> : null}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">
            {updating ? "Checking…" : "No result yet — press Check."}
          </Typography.Text>
        )}
        {shown ? (
          <Table
            size="small"
            pagination={false}
            rowKey="key"
            dataSource={shown.groups}
            columns={columns}
            style={updating ? { opacity: 0.5 } : undefined}
          />
        ) : null}

        <Card size="small" title="Recover">
          <Space direction="vertical" style={{ width: "100%" }}>
            <DatePicker.RangePicker
              format="YYYY-MM-DD"
              value={range as any}
              onChange={(v) => setRange(v as any)}
              disabledDate={(d) => !d || !abnormal.has(d.format("YYYY-MM-DD"))}
              disabled={!shown || abnormal.size === 0}
            />
            <Typography.Text type="secondary">
              Only abnormal dates can be picked as the ends of the range. Starts the Crypto, Nasdaq and Funds jobs for
              every day of the range; existing values are kept (override is off here).
            </Typography.Text>
            <Button
              type="primary"
              onClick={recover}
              loading={recovering}
              disabled={!range || !range[0] || !range[1] || anyActive}
            >
              Recover
            </Button>
            {anyActive ? (
              <Typography.Text type="secondary">A recovery job is running; wait for it to finish.</Typography.Text>
            ) : null}
            {recoverResult
              ? recoverResult.map((j) => {
                  const label = (JOBS.find((x) => x.type === j.type) || { label: j.type }).label;
                  return j.started ? (
                    <Typography.Text key={j.type}>
                      {label}: {j.deduplicated ? "already running or queued" : "started"}
                    </Typography.Text>
                  ) : (
                    <Typography.Text key={j.type} type="danger">
                      {label}: not started — {j.error}
                    </Typography.Text>
                  );
                })
              : null}
            <Space wrap>
              {JOBS.map((j, i) => (
                <span key={j.type}>
                  {j.label}: {jobList[i] ? <JobStatusTag job={jobList[i] as BatchJob} /> : <Tag>no job</Tag>}
                </span>
              ))}
            </Space>
          </Space>
        </Card>
      </Space>
    </Card>
  );
};

export default HistoryGapsCard;
